import { useRef, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

export interface TabItem {
  id: string;
  label: ReactNode;
  disabled?: boolean;
}

interface TabsProps {
  items: readonly TabItem[];
  value: string;
  onChange: (id: string) => void;
  ariaLabel: string;
  className?: string;
}

/**
 * Underline tabs — the only tab treatment shipped by v2.1. Keyboard-nav
 * respects the WAI-ARIA Authoring Practices for horizontal tabs: Left/Right
 * cycles focus, Home/End jumps, Enter/Space activates.
 */
export function Tabs({ items, value, onChange, ariaLabel, className }: TabsProps): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);

  function focusIndex(idx: number): void {
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    if (!buttons || buttons.length === 0) return;
    const clamped = ((idx % buttons.length) + buttons.length) % buttons.length;
    buttons[clamped]?.focus();
  }

  function handleKey(event: KeyboardEvent<HTMLButtonElement>, currentIdx: number): void {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        focusIndex(currentIdx + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        focusIndex(currentIdx - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusIndex(0);
        break;
      case 'End':
        event.preventDefault();
        focusIndex(items.length - 1);
        break;
      default:
    }
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn('flex items-end gap-6 border-b border-rule', className)}
    >
      {items.map((item, idx) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`tabpanel-${item.id}`}
            id={`tab-${item.id}`}
            tabIndex={active ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            onKeyDown={(e) => handleKey(e, idx)}
            className={cn(
              '-mb-px border-b-2 pb-2 pt-1.5 text-xs font-medium ' +
                'transition-colors duration-instant ease-standard ' +
                'focus-visible:outline-none focus-visible:text-ink ' +
                'disabled:cursor-not-allowed disabled:opacity-40',
              active
                ? 'border-ink text-ink'
                : 'border-transparent text-ink-muted hover:text-ink-soft',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

interface TabPanelProps {
  id: string;
  active: boolean;
  children: ReactNode;
  className?: string;
}

export function TabPanel({ id, active, children, className }: TabPanelProps): JSX.Element | null {
  if (!active) return null;
  return (
    <div
      role="tabpanel"
      id={`tabpanel-${id}`}
      aria-labelledby={`tab-${id}`}
      tabIndex={0}
      className={cn('focus-visible:outline-none', className)}
    >
      {children}
    </div>
  );
}
